import { useEffect, useMemo, useState } from 'react';
import type { ArcTemplateRow } from 'types/schema';
import type { StageDefinition } from 'types/arc';
import { useApiClientsOrNull } from '../context/ApiClientsContext';

/**
 * One arc template as the filter UI consumes it: enough to populate the arc
 * selector (system name + display name) and, once chosen, the stage picker
 * (`definition.stages` — see `buildFilterArcOptions`).
 */
/** The slice of a stage the pickers read: its id and the pair that names it. */
export type ArcTemplateStageSummary = Pick<StageDefinition, 'id' | 'localizationKey' | 'fallbackName'>;

export interface ArcTemplateOption {
  systemName: string;
  name: string;
  version: number;
  definition?: { stages: ArcTemplateStageSummary[] };
  /**
   * Discovery metadata the type composer's arc picker shows beside each
   * template: its category, and the applicability its own gate will evaluate at
   * deploy time. `applicability` is carried so the picker can say up front that
   * a template will not deploy onto this type (`validateApplicability`) — a
   * discovery affordance, never the gate itself.
   */
  category?: string;
  applicability?: ArcTemplateRow['applicability'];
}

/**
 * Collapse the raw rows to one entry per `system_name`, keeping the highest
 * version. `GET /arc-templates/` returns every published version of every
 * template, so without this the selector would list the same template once per
 * version — and a stage picker fed by an older version's `definition.stages`
 * would offer stage ids the live arcs no longer use.
 *
 * Mirrors `fetchPublishedArcTemplate`'s version-sort, which the existing
 * by-systemName path already relies on.
 */
function toLatestVersionOptions(rows: ArcTemplateRow[]): ArcTemplateOption[] {
  const latest = new Map<string, ArcTemplateRow>();
  for (const row of rows) {
    if (!row?.system_name) continue;
    const existing = latest.get(row.system_name);
    if (!existing || row.version > existing.version) latest.set(row.system_name, row);
  }
  return Array.from(latest.values())
    .map((row) => ({
      systemName: row.system_name,
      name: row.name,
      version: row.version,
      definition: row.definition,
      category: row.category,
      applicability: row.applicability,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Lists the published arc templates the caller can see, for the arc filter
 * lane's selector + stage picker.
 *
 * Reads the existing `GET /arc-templates/` list endpoint, which is already
 * permission-checked and org-scoped server-side (system + org + subscribed
 * templates). The catalog it returns is a discovery affordance, not a gate —
 * the execute engine remains the authority on what an authored arc filter
 * actually matches.
 *
 * @param enabled - when false, skips the fetch entirely (e.g. the arc lane is
 *   not rendered). Avoids a list request on every editor mount.
 */
export function useArcTemplates(enabled = true): {
  templates: ArcTemplateOption[];
  loading: boolean;
  error: boolean;
} {
  const apiClients = useApiClientsOrNull();
  const schemaClient = apiClients?.schemaClient;

  const [rows, setRows] = useState<ArcTemplateRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!enabled || !schemaClient) {
      setRows(null);
      setLoading(false);
      setError(false);
      return;
    }

    setLoading(true);
    setError(false);

    schemaClient
      .get<ArcTemplateRow[]>('/arc-templates/')
      .then((result) => {
        if (cancelled) return;
        setRows(Array.isArray(result) ? result : []);
      })
      .catch(() => {
        // A failed list leaves the selector empty rather than breaking the
        // editor — an author can still save every other lane.
        if (cancelled) return;
        setRows([]);
        setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, schemaClient]);

  const templates = useMemo(() => toLatestVersionOptions(rows ?? []), [rows]);

  return { templates, loading, error };
}

export default useArcTemplates;
